import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { CreateRecordProperties, PopupData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';

import GroupPopupEditComponent from 'webapp/components/group/popup-edit';

import Group from 'webapp/models-custom/group';
import type GroupRecord from 'webapp/models/group';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export default class ModelManagerGroupsService extends ModelManagerForStoreService<
	GroupRecord,
	Group
> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;

	private _groupModels: Group[] = [];
	@cached
	get groupModels(): Group[] {
		this.updateModelCache(this._groupModels, this.savedRecords);
		return this._groupModels;
	}

	openCreatePopup(onSave: PopupData.Group.Edit['onSave']) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const model = this.createNewModel(),
			popup = this.popupService.createModal(GroupPopupEditComponent, {
				popupClass: 'popup-type-group-edit',
			});

		popup.open({
			model,
			onSave,
		});
	}

	// ------ Requests ------

	_groupsRequested = false;
	_groupsRequestPromise: Promise<void> | null = null;
	requestGroups(): Promise<void> {
		if (this.currentUserService.isViewer) {
			return Promise.resolve();
		}

		const variablePrefix = 'groups';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.storeFindAll(ModelName.GROUP);
			},
		);
	}

	// ------ For-store ------

	modelName = ModelName.GROUP;

	override createNewModel(properties: CreateRecordProperties<GroupRecord> = {}): Group {
		if (!properties.color) {
			properties.color = this.mmBoardsService.getRandomColor();
		}
		return super.createNewModel(properties);
	}

	createModel(model: GroupRecord): Group {
		return new Group({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/groups': ModelManagerGroupsService;
	}
}
